<script>
    import hljs from "highlight.js";
    import 'highlight.js/styles/monokai.css';
    
    function highlight_code(node) {
        hljs.highlightElement(node, {language: "JavaScript"})
        return {
            update: () => {
                hljs.highlightElement(node, {language: "JavaScript"})
            }
        }
    }

    import Katex from "../Katex.svelte";
    import HilbertCurve from "./HilbertCurve.svelte";
    import GosperCurve from "./GosperCurve.svelte";
</script>

<section>
    <h2>Runtime Evaluation</h2>

    <section>
        <div>
            <ul>
                <li class="fragment">    
                    recursive construction rules<br>
                    <div style="display: flex; gap: 2em; justify-content: center;">
                        <div style="display: inline-block;">
                            <HilbertCurve W={120} H={120} level=1 /><br>
                            <small>Hilbert curve</small>
                        </div>
                        
                        <div style="display: inline-block;">
                            <GosperCurve W={120} H={120} level=1 /><br>
                            <small>Gosper curve</small>
                        </div>
                    </div>
                </li>
                <li class="fragment">minimum level: <Katex>{`l_{min} = \\lceil \\log_{4 | 7} n\\rceil`}</Katex></li>
                <li class="fragment">computing one index → <Katex>{`\\cal{O}(\\log n)`}</Katex></li>
            </ul>
        </div>
        <aside class="notes">
            First we did a theoretical runtime evaluation.
            The runtime is mainly dependend on the number of data points to gridify. ✨
            The Hilbert and Gosper curves have recursive construction rules. They start with a pattern with a number of available spots.
            The Hilbert Curve with 4 and the Gosper Curve with 7. Each level deeper, the number of available spots gets multiplied by
            -- in the case of the hilbert curve -- 4, and
            -- in the case of the gosper curve --- 7✨
            So, the minimum level for the SFC with enough spots for the data can be computed. 
            The log to the base of 4 or 7 of "n": the number of points, and the result rounded up to the next integer.✨
            The algorithm to compute one index needs O log n`} runtime.
        </aside>
    </section>
    <section>
            <div class="row">
                <div class="column">
                    <pre use:highlight_code>
                        <code class="language-javascript">  
<i>// align points to grid defined by sfc</i>
{`for (let point of scatterplot) {
    spot = compute_sfc_index(point);
    if (spot empty) {
        place point;
    } else {
        collision(point);
    }
}`}
<i>// compute 2d positions</i>
</code>
                    </pre>
                    <center>
                        Alg. 1: placing <Katex>n</Katex> points
                    </center>
                    <small class="fragment"><Katex>{`n \\cdot \\cal{O}(\\log n) = \\cal{O}(n \\log n)`}</Katex></small>
                </div>
                <div class="fragment column">
                    <pre use:highlight_code>
                        <code>{`
pred = spot - 1;
succ = spot + 1;
while (no empty and valid spot) {
    if ((pred || succ) empty and valid) {
        place point;
        return;
    }
    pred--;
    succ++;
}`}</code>
                    </pre>
                    <center>
                        Alg. 2: resolving collisions<br>
                    </center>

                    <small class="fragment">
                        best case: 
                        <Katex>{`\\cal{O}(1)`}</Katex><br>
                        worst case: 
                        <Katex>{`\\cal{O}(n)`}</Katex>
                    </small>
                </div>
          </div>
          <aside class="notes">
                Algorithm 1 just places images if the respective spot is empty, if not it calls the second algorithm for resolving the collision.✨
                computing the spot for one point need O log n runtime, so placing all n points need O n log n runtime.✨
                The runtime for the collision is in the best case O 1, if the next or the previos spot is available. In the worst case, 
                algorithm 2 finds all already occupied spots until it finds an empty spot, so algorithm 2 has in the worst case a runtime of O n.
          </aside>
      </section>
      <section>
            <div>
                <p>best case: <Katex>{`\\cal{O}(n \\log n)`}</Katex>; worst case: <Katex>{`\\cal{O}(n^2 \\log n)`}</Katex></p>
                <img class="fragment" src="./images/runtime_evaluation.svg" alt="runtime evaluation" />
                <img class="fragment" src="./images/collisions_evaluation.svg" alt="collisions evaluation" />
            </div>
            <aside class="notes">
                So, all in all, the best case for hagrid is O (n log n), the worst case O (n squared) log n. ✨
                We created randomly, uniformly distributed, placed points of datasets with
                sizes from a thousend points up to ten thousend points with a stepsize of a hundred points.
                We measured the runtimes and number of collisions for Hagrid gridifying those scatterplots, using the hilbert curve and the gosper curve.✨
                We computed the grid layouts for each dataset twenty times, shown as colored dots in the chart, and 
                The lines show the averages of those measurements for the used space filling curve. ✨
                The second chart shows the number of collisions happend, here we used a log scale. The spikes appear when the
                respective SFC reached the maximum allowed points for the used level.
                The charts show that -- as expected -- more collisions increase the runtimes slightly.
            </aside>

      </section>
</section>

<section>
    <h2>Evaluation</h2>
    <section>
        <div>
            <ul>
                <li class="fragment">DGrid → space filling layouts</li>
                <li class="fragment">SMWG → grid layouts of maps</li>
                <li class="fragment">CMDS → grid layouts of scatterplots</li>
            </ul>
        </div>
        <aside class="notes">
            We also compared Hagrid to other techniques.
            With DGrid, which produces space filling layouts✨, with SMWG which is used for creating grid layouts of geographical areas with a strong
            focus on quality✨, and CMDS which also produces grid layouts as our approach at seemingly much higher runtimes✨.  
        </aside>
    </section>
    <section>
        <img class="fragment" src="./images/table_summary.svg" alt="summary quality evaluation" />
        <aside class="notes">
            We used various datasets with a wide range of dataset sizes, computed various dimensionality reduced projections.
            For evaluation, We used 5 different metrics to measure those resulting grid layouts. ✨
            We used
            NP: the neighborhood preservation
            CC: the cross correlation
            SI: size increase
            ED: euclidean distances
            and also the runtime.
            Hagrid is shown here in purples, and the other gridifying techniques with yellow and orange hues.
            
            The chart shows small boxplots, with the mean result as white line.
        </aside>
    </section>

    <section data-transition="fade">
        <h5>runtime</h5>
        <div class="columns">
            <div class="fragment">
                <img src="./images/table_labels.svg" alt="summary quality evaluation" />
                <small>coil-100 dataset</small>
            </div>
            <img src="./images/table_rt.svg" alt="summary quality evaluation" />
        </div>
        <aside class="notes">
            Here, on the right side, is again the comparison result of the runtimes.
            We show SMWG only for the smallest dataset, because the runtimes for those sizes are already very high, CMDS for the first
            two groups of dataset sizes, which has a better runtime than SMWG, but is apparantly still very slow. 
            DGrid is very fast, but through all dataset sizes roughly four times slower than Hagrid. ✨
            I use for illustration a bigger subset of the already previous used coil-100 dataset. 
            Where Hagrid keeps the global structure, whereas DGrid (which has a space filling approach) fills the space. 
            Which has the advantage of bigger grid cells, but comes with a loss of the global structure of the scatterplot.
        </aside>
    </section>
    <section data-transition="fade">
        <h5>neighborhood preservation</h5>
        <div class="columns">
            <div>
                <img src="./images/table_qualitative_np.svg" alt="qnp" />
                <small>coil-100 dataset</small>
            </div>
            <img src="./images/table_np.svg" alt="summary quality evaluation" />
        </div>
        <aside class="notes">
            SMWG has the best results for neighborhood preservation for the smaller datasets. 
            Neighborhood preservation is a very important metric for evaluating techniques for gridifying geographical areas.
            CMDS has the best results for the next bigger dataset sizes. After that, hagrid has the best results. ✨
            On the left, each single point of the dataset is colored by the value of the neighborhood preservation, 
            where orange means no neighborhood preservation and purple complete neighborhood preservation.
        </aside>
    </section>
    <section data-transition="fade">
        <h5>cross correlation</h5>
        <div class="columns">
            <div>
                <img src="./images/table_qualitative_cc.svg" alt="qnp" />
                <small>coil-100 dataset</small>
            </div>
            <img src="./images/table_cc.svg" alt="summary quality evaluation" />
        </div>
        <aside class="notes">
            With the cross correlation metric, Hagrid has the best results for all dataset sizes.
            In the example on the left, the hagrid results have very good values, and DGrid has one spot with worse cross correlation.
        </aside>
    </section>
    <section data-transition="fade">
        <h5>euclidean distance</h5>
        <div class="columns">
            <div>
                <img src="./images/table_qualitative_ed.svg" alt="qnp" />
                <small>coil-100 dataset</small>
            </div>
            <img src="./images/table_ed.svg" alt="summary quality evaluation" height="80%" />
        </div>
        <aside class="notes">
            The lines on the left connect the original positions of a point to its gridified position, and the color 
            shows the euclidean distance.
            Hagrid as throughout all dataset sizes the best results for the euclidean distance metric.
        </aside>
    </section>
</section>

<style>
    .row {
        display: grid;
        grid-template-columns: 0.9fr 1fr;
        gap: .2em;
    }

    .column > pre{
        min-height: 60% !important;
        font-size: 0.45em;
        font-family: "Victor Mono" !important;
    }

    :global(.hljs-comment) {
        font-style: italic;
    }

    .columns {
        display: flex; 
        gap: 2em; 
        height: 50%;
        align-items: center;
    }

    .columns > img {
        margin: 0;
    }

    .columns > div {
        width: 60%;
    }
</style>